import { reactive } from 'vue';

const DEFAULT_DURATION = 5000;
let nextId = 0;

export const undoState = reactive({ entries: [] });

function findEntry(id) {
  return undoState.entries.find((e) => e.id === id) || null;
}

function drop(id) {
  const index = undoState.entries.findIndex((e) => e.id === id);
  if (index !== -1) undoState.entries.splice(index, 1);
}

async function runCommit(entry) {
  entry.committing = true;
  try {
    await entry.commitFn?.();
    entry.committing = false;
    drop(entry.id);
    return true;
  } catch {
    entry.committing = false;
    entry.error = true;
    return false;
  }
}

export function show(message, commitFn, undoFn, { duration = DEFAULT_DURATION } = {}) {
  nextId += 1;
  const entry = reactive({
    id: nextId,
    message,
    commitFn,
    undoFn,
    settled: false,
    error: false,
    committing: false,
    undone: false,
    timer: null,
  });
  entry.timer = setTimeout(() => settle(entry.id), duration);
  undoState.entries.push(entry);
  return entry.id;
}

export function settle(id) {
  const entry = findEntry(id);
  if (!entry || entry.settled) return Promise.resolve(true);
  entry.settled = true;
  clearTimeout(entry.timer);
  return runCommit(entry);
}

export function undo(id) {
  const entry = findEntry(id);
  if (!entry || entry.settled) return;
  entry.settled = true;
  clearTimeout(entry.timer);
  entry.undoFn?.();
  entry.undone = true;
  setTimeout(() => drop(id), 1500);
}

export function retry(id) {
  const entry = findEntry(id);
  if (!entry) return Promise.resolve(false);
  entry.error = false;
  return runCommit(entry);
}

export function dismiss(id) {
  const entry = findEntry(id);
  if (!entry) return;
  if (entry.error) {
    entry.undoFn?.();
    drop(id);
    return;
  }
  if (entry.undone) {
    drop(id);
    return;
  }
  settle(id);
}

export function settleAll() {
  for (const entry of [...undoState.entries]) {
    if (!entry.settled) settle(entry.id);
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', settleAll);
}

export default { show, undo, retry, dismiss, settle, settleAll, undoState };
